import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Loader2, Radio, Trophy, Users, Calendar, MapPin, Sparkles, AlertCircle, Flame, Eye } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 3000;

/**
 * PUBLIC live leaderboard page (Phase 2).
 *
 * Data source strategy:
 *  1) Try WebSocket at /api/ws/live-leaderboard/{link_id} — pushes every ~2s.
 *  2) When WS is disconnected, fall back to HTTP polling every 3s.
 *  3) A ref-based wsAlive flag decides which is active; polling never runs
 *     concurrently with a healthy WS to avoid double-fetching.
 *
 * useEffect deps are PRIMITIVES only (linkId string, no event objects) —
 * this is the pattern that prevents the "reconnect on every render" flicker.
 */
export default function LiveLeaderboardPage() {
  const { linkId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [wsAlive, setWsAlive] = useState(false);

  // Refs: things the WS/polling handlers need without triggering re-renders.
  const wsAliveRef = useRef(false);
  const wsRef = useRef(null);
  const wsClosedRef = useRef(false);
  const reconnectTimerRef = useRef(null);
  const pollTimerRef = useRef(null);

  // ─── HTTP fallback fetch ────────────────────────────────────────────────
  const fetchOnce = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/live-leaderboard/${linkId}`);
      setData(res.data);
      setError('');
    } catch (err) {
      if (err.response?.status === 404) {
        setError('This live leaderboard link is invalid or has been removed.');
      } else {
        setError('Reconnecting…');
      }
    } finally {
      setLoading(false);
    }
  }, [linkId]);

  // ─── WebSocket connect (with reconnect backoff) ─────────────────────────
  const connectWS = useCallback(() => {
    if (wsClosedRef.current) return;
    const wsUrl = `${API_URL.replace(/^http/, 'ws')}/api/ws/live-leaderboard/${linkId}`;
    let ws;
    try {
      ws = new WebSocket(wsUrl);
    } catch {
      scheduleReconnect();
      return;
    }
    wsRef.current = ws;
    ws.onopen = () => {
      wsAliveRef.current = true;
      setWsAlive(true);
      // Stop HTTP polling while WS is healthy.
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg?.type === 'event_removed') {
          setError('This event has ended and is no longer available.');
          return;
        }
        if (msg?.type === 'error') {
          setError(msg.detail || 'Not found.');
          return;
        }
        // snapshot payload (has same shape as GET /api/live-leaderboard/…)
        // First snapshot may commit before onopen — flip wsAlive here too so
        // the badge tooltip doesn't briefly say "Reconnecting…".
        if (!wsAliveRef.current) {
          wsAliveRef.current = true;
          setWsAlive(true);
          if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
          }
        }
        setData(msg);
        setError('');
        setLoading(false);
      } catch {
        /* ignore malformed */
      }
    };
    ws.onclose = () => {
      wsAliveRef.current = false;
      setWsAlive(false);
      scheduleReconnect();
    };
    ws.onerror = () => {
      // onclose will fire right after, keep behavior in one place.
    };
  }, [linkId]);

  const scheduleReconnect = useCallback(() => {
    if (wsClosedRef.current) return;
    // Restart HTTP polling as fallback while WS is down.
    if (!pollTimerRef.current) {
      fetchOnce();
      pollTimerRef.current = setInterval(fetchOnce, POLL_INTERVAL_MS);
    }
    if (reconnectTimerRef.current) return;
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      connectWS();
    }, 3000);
  }, [connectWS, fetchOnce]);

  // Lifecycle — depends only on the primitive linkId string.
  useEffect(() => {
    wsClosedRef.current = false;
    // Prime with one HTTP fetch so we render immediately even before WS opens.
    fetchOnce();
    connectWS();
    return () => {
      wsClosedRef.current = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      try { wsRef.current?.close(); } catch { /* noop */ }
      wsRef.current = null;
    };
  }, [linkId, fetchOnce, connectWS]);

  // Facebook link-preview: dynamic <title>.
  useEffect(() => {
    const name = data?.event?.name || 'Live Leaderboard';
    document.title = `${name} · Brain Gym Jr.`;
  }, [data?.event?.name]);

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-[#0f172a] text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }
  if (error && !data) {
    return (
      <div className="min-h-screen bg-[#0f172a] text-white flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <AlertCircle className="w-14 h-14 text-[#f59e0b] mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Not found
          </h1>
          <p className="text-white/70">{error}</p>
        </div>
      </div>
    );
  }

  const stage = data?.stage || 'upcoming';
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0f172a] via-[#1e293b] to-[#0f172a] text-white">
      <Header
        event={data?.event}
        stage={stage}
        participantsCount={data?.participants_count}
        viewers={data?.viewers || 0}
        wsAlive={wsAlive}
      />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 pb-16">
        {stage === 'upcoming' && <UpcomingView data={data} />}
        {stage === 'live'     && <LiveView     data={data} />}
        {stage === 'ended'    && <EndedView    data={data} />}
      </main>
      <Footer />
    </div>
  );
}

// ─── HEADER ──────────────────────────────────────────────────────────────────
const Header = ({ event, stage, participantsCount, viewers, wsAlive }) => (
  <header className="max-w-3xl mx-auto px-4 sm:px-6 pt-8 pb-6 text-center">
    <div className="flex items-center justify-center gap-2 mb-3">
      <img src="/logo.png" alt="Brain Gym Jr." className="w-10 h-10 object-contain" />
      <span className="text-sm font-bold tracking-widest text-white/80" style={{ fontFamily: 'Fredoka, sans-serif' }}>
        BRAIN GYM JR.
      </span>
    </div>
    <StageBadge stage={stage} wsAlive={wsAlive} />
    <h1 className="text-3xl sm:text-4xl font-bold mt-3 mb-1" style={{ fontFamily: 'Fredoka, sans-serif' }} data-testid="live-event-name">
      {event?.name || 'Live Leaderboard'}
    </h1>
    <p className="text-white/60 text-sm flex items-center justify-center gap-4 flex-wrap">
      {event?.event_date && <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{event.event_date}{event?.event_start_time ? ` · ${event.event_start_time}` : ''}</span>}
      {event?.venue && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{event.venue}</span>}
      {typeof participantsCount === 'number' && participantsCount > 0 && (
        <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" />{participantsCount} participants</span>
      )}
      {viewers > 0 && (
        <span className="inline-flex items-center gap-1 text-[#22c55e]" data-testid="live-viewers-count">
          <Eye className="w-3.5 h-3.5" />{viewers} watching
        </span>
      )}
    </p>
  </header>
);

const StageBadge = ({ stage, wsAlive }) => {
  const map = {
    upcoming: { label: 'Starting Soon', bg: 'bg-[#facc15]/20', text: 'text-[#facc15]', border: 'border-[#facc15]/40', pulse: true },
    live:     { label: 'LIVE',           bg: 'bg-[#ef4444]/20', text: 'text-[#ef4444]', border: 'border-[#ef4444]/40', pulse: true },
    ended:    { label: 'Final Results',  bg: 'bg-[#22c55e]/20', text: 'text-[#22c55e]', border: 'border-[#22c55e]/40', pulse: false },
  }[stage] || { label: '—', bg: 'bg-white/10', text: 'text-white/70', border: 'border-white/20', pulse: false };
  return (
    <span
      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest border ${map.bg} ${map.text} ${map.border}`}
      data-testid="live-stage-badge"
      title={wsAlive ? 'Real-time connection active' : 'Reconnecting…'}
    >
      {map.pulse && <Radio className="w-3 h-3 animate-pulse" />}
      {map.label}
    </span>
  );
};

// ─── UPCOMING ────────────────────────────────────────────────────────────────
const UpcomingView = ({ data }) => {
  const target = buildTargetDate(data?.event?.event_date, data?.event?.event_start_time);
  const [remaining, setRemaining] = useState(() => diffToUnits(target));
  useEffect(() => {
    if (!target) return;
    const id = setInterval(() => setRemaining(diffToUnits(target)), 1000);
    return () => clearInterval(id);
  }, [target && target.getTime()]);

  return (
    <section className="mt-4" data-testid="live-upcoming-view">
      <div className="text-center mb-6">
        <Sparkles className="w-6 h-6 text-[#facc15] mx-auto mb-2" />
        <p className="text-lg text-white/90 font-medium">Competition starts in</p>
      </div>
      <div className="grid grid-cols-4 gap-3 sm:gap-4 max-w-2xl mx-auto">
        {[
          { label: 'Days',    val: remaining.days },
          { label: 'Hours',   val: remaining.hours },
          { label: 'Minutes', val: remaining.minutes },
          { label: 'Seconds', val: remaining.seconds },
        ].map((u, i) => (
          <div key={i} className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl py-6 text-center">
            <div className="text-4xl sm:text-5xl font-bold text-white tabular-nums" style={{ fontFamily: 'Fredoka, sans-serif' }} data-testid={`countdown-${u.label.toLowerCase()}`}>
              {u.val.toString().padStart(2, '0')}
            </div>
            <div className="text-[10px] sm:text-xs uppercase tracking-widest text-white/60 mt-2">{u.label}</div>
          </div>
        ))}
      </div>
      <p className="text-center text-white/70 mt-8 text-sm">
        Stay tuned — this same link becomes the live leaderboard when the event goes live.
      </p>
    </section>
  );
};

function buildTargetDate(dateStr, timeStr) {
  if (!dateStr) return null;
  const d = new Date(`${dateStr}T${timeStr || '10:00'}:00`);
  return isNaN(d.getTime()) ? null : d;
}
function diffToUnits(target) {
  if (!target) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  const diff = Math.max(0, target.getTime() - Date.now());
  const s = Math.floor(diff / 1000);
  return {
    days:    Math.floor(s / 86400),
    hours:   Math.floor((s % 86400) / 3600),
    minutes: Math.floor((s % 3600) / 60),
    seconds: s % 60,
  };
}

// ─── LIVE ────────────────────────────────────────────────────────────────────
const LiveView = ({ data }) => {
  const rows = data?.leaderboard || [];
  if (!rows.length) {
    return (
      <section className="mt-4 text-center" data-testid="live-live-view-empty">
        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-white/70" />
        <p className="text-white/70">Waiting for the first scores to roll in…</p>
      </section>
    );
  }
  return (
    <section className="mt-4" data-testid="live-live-view">
      {/* AnimatePresence + layout on each row → framer-motion FLIPs each row
          to its new Y when the sort order changes → smooth "rank swoosh". */}
      <motion.ul className="space-y-2" layout>
        <AnimatePresence initial={false}>
          {rows.map((r) => (
            <LeaderboardRow key={r.participant_id} row={r} />
          ))}
        </AnimatePresence>
      </motion.ul>
    </section>
  );
};

/** Number that smoothly rolls to `value` on change. Pure custom hook, no lib. */
function useRollingNumber(value, durationMs = 700) {
  const [display, setDisplay] = useState(value);
  const rafRef = useRef(null);
  const fromRef = useRef(value);
  const startRef = useRef(0);
  useEffect(() => {
    fromRef.current = display;
    startRef.current = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - startRef.current) / durationMs);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      const next = Math.round(fromRef.current + (value - fromRef.current) * eased);
      setDisplay(next);
      if (t < 1) rafRef.current = requestAnimationFrame(step);
    };
    if (value !== display) rafRef.current = requestAnimationFrame(step);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return display;
}

const LeaderboardRow = ({ row }) => {
  const rank = row.rank;
  const isTop = rank <= 3;
  const rolling = useRollingNumber(row.score);

  // Top-3 gold/silver/bronze glow variants
  const styles =
    rank === 1
      ? { bg: 'bg-gradient-to-r from-[#facc15] to-[#f59e0b]', ring: 'shadow-[0_0_35px_rgba(250,204,21,0.5)]', badge: 'bg-[#0f172a]/20 text-white' }
      : rank === 2
      ? { bg: 'bg-gradient-to-r from-[#e2e8f0] to-[#94a3b8]', ring: 'shadow-[0_0_25px_rgba(226,232,240,0.35)]', badge: 'bg-[#0f172a]/25 text-white' }
      : rank === 3
      ? { bg: 'bg-gradient-to-r from-[#d97706] to-[#92400e]', ring: 'shadow-[0_0_22px_rgba(217,119,6,0.35)]', badge: 'bg-white/20 text-white' }
      : { bg: 'bg-white/5', ring: '', badge: 'bg-white/10 text-white/80' };

  return (
    <motion.li
      layout
      layoutId={`row-${row.participant_id}`}
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -14 }}
      transition={{ type: 'spring', stiffness: 400, damping: 32 }}
      className={`flex items-center gap-3 sm:gap-4 rounded-2xl p-3 sm:p-4 border ${isTop ? 'border-white/25' : 'border-white/10'} ${styles.bg} ${styles.ring}`}
      data-testid={`live-row-rank-${rank}`}
    >
      <div className={`relative flex-shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-full flex items-center justify-center font-bold text-lg ${styles.badge}`}>
        {rank === 1 ? (
          <motion.div
            initial={{ y: -2 }}
            animate={{ y: [-2, -6, -2] }}
            transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Trophy className="w-5 h-5 sm:w-6 sm:h-6" />
          </motion.div>
        ) : (
          `#${rank}`
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-white truncate flex items-center gap-1.5" style={{ fontFamily: 'Fredoka, sans-serif' }}>
          <span className="truncate">{row.display_name}</span>
          {row.streak >= 3 && (
            <motion.span
              initial={{ scale: 0.8, opacity: 0.7 }}
              animate={{ scale: [1, 1.15, 1], opacity: 1 }}
              transition={{ duration: 0.9, repeat: Infinity }}
              className="inline-flex items-center gap-0.5 text-[#f97316] text-xs font-bold flex-shrink-0"
              data-testid={`live-row-streak-${rank}`}
              title={`${row.streak} in a row`}
            >
              <Flame className="w-3.5 h-3.5" />
              {row.streak}
            </motion.span>
          )}
        </div>
        <div className="text-xs text-white/60 flex items-center gap-2 flex-wrap mt-0.5">
          <span>{row.correct} correct</span>
          {row.current_round > 0 && <span>· Round {row.current_round}</span>}
        </div>
      </div>
      <div className="flex-shrink-0 text-right">
        <div className="text-2xl sm:text-3xl font-bold text-white tabular-nums" style={{ fontFamily: 'Fredoka, sans-serif' }}>
          {rolling}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-white/60">points</div>
      </div>
    </motion.li>
  );
};

// ─── ENDED ───────────────────────────────────────────────────────────────────
const EndedView = ({ data }) => {
  const rows = data?.leaderboard || [];
  const winner = data?.winner;
  return (
    <section className="mt-4" data-testid="live-ended-view">
      {winner && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: 'spring', stiffness: 260, damping: 22 }}
          className="text-center bg-gradient-to-br from-[#facc15] to-[#f59e0b] rounded-3xl p-6 sm:p-8 mb-8 shadow-[0_0_50px_rgba(250,204,21,0.35)]"
        >
          <motion.div
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Trophy className="w-14 h-14 text-[#0f172a] mx-auto mb-2" />
          </motion.div>
          <p className="text-xs uppercase tracking-widest text-[#0f172a]/70 font-bold">Champion</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0f172a] mt-1" style={{ fontFamily: 'Fredoka, sans-serif' }} data-testid="live-winner-name">
            {winner.display_name}
          </h2>
          <p className="text-[#0f172a]/80 mt-2 font-medium">
            {winner.score} points · {winner.correct} correct
          </p>
        </motion.div>
      )}
      {rows.length > 0 && (
        <>
          <h3 className="text-sm uppercase tracking-widest text-white/60 font-bold mb-3">Final Standings</h3>
          <motion.ul className="space-y-2" layout>
            <AnimatePresence initial={false}>
              {rows.map((r) => <LeaderboardRow key={r.participant_id} row={r} />)}
            </AnimatePresence>
          </motion.ul>
        </>
      )}
      {rows.length === 0 && (
        <p className="text-center text-white/70">Final results are being compiled…</p>
      )}
    </section>
  );
};

// ─── FOOTER ──────────────────────────────────────────────────────────────────
const Footer = () => (
  <footer className="text-center text-xs text-white/40 py-6 border-t border-white/5">
    © Brain Gym Jr. Olympiad · Live spectator view
  </footer>
);
